import React from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ChevronRight, Check, Sparkles, BarChart3 } from "lucide-react";
import { Reveal } from "./Section";
import { useQuote } from "./QuoteContext";

const arguments_list = [
  {
    icon: TrendingUp,
    title: "Déductibilité fiscale immédiate",
    body: "Chaque euro versé réduit votre revenu imposable. Avec une TMI à 30 %, 10 000 € versés = 3 000 € récupérés sur votre impôt.",
  },
  {
    icon: Sparkles,
    title: "Liberté des supports",
    body: "Contrairement aux PER bancaires, je sélectionne les meilleures unités de compte et fonds euros du marché, sans conflit d'intérêt.",
  },
  {
    icon: BarChart3,
    title: "Sortie en capital à la retraite",
    body: "Récupérez votre épargne en une fois ou en rente, selon votre stratégie patrimoniale.",
  },
];

export default function PERSection() {
  const { openQuote } = useQuote();
  return (
    <section
      id="per-section"
      data-testid="per-section"
      className="relative py-24 md:py-32 bg-brand-sand-2 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-brand-ink text-white flex items-center justify-center">
                <TrendingUp className="h-4 w-4" />
              </div>
              <span className="overline">PER · Épargne retraite & défiscalisation</span>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.02] text-brand-ink">
              Transformez vos impôts en{" "}
              <span className="italic font-editorial">patrimoine</span>.
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-5 text-brand-ink/70 text-lg md:text-xl leading-relaxed font-light">
              Jusqu'à <span className="font-semibold text-brand-ink">45 % de vos versements</span>{" "}
              financés par l'État.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-8">
          {arguments_list.map((a, i) => (
            <Reveal key={a.title} delay={i}>
              <div
                data-testid={`per-argument-${i}`}
                className="card-lift bg-white rounded-2xl border border-brand-border p-8 h-full"
              >
                <div className="h-11 w-11 rounded-full bg-brand-ink text-white flex items-center justify-center mb-5">
                  <a.icon className="h-4 w-4" />
                </div>
                <h3 className="font-serif text-[22px] tracking-tight leading-tight text-brand-ink">
                  {a.title}
                </h3>
                <p className="mt-3 text-brand-ink/70 text-[15px] leading-relaxed">{a.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={3}>
          <div className="mt-14 grid md:grid-cols-[1.4fr_1fr] gap-8 md:gap-12 items-center">
            <div className="bg-white rounded-2xl border border-brand-border p-8 md:p-10">
              <div className="overline mb-3">L'avis du courtier</div>
              <p className="font-editorial italic text-xl md:text-[22px] leading-snug text-brand-ink">
                « Votre banque vous proposera son PER maison — un seul produit, une seule gamme. Moi, courtier
                indépendant ORIAS, je compare l'ensemble du marché selon votre profil fiscal. Aucun lien avec un
                assureur. Uniquement votre intérêt. »
              </p>
              <div className="mt-5 text-sm text-brand-ink/65 font-semibold">
                — Nassim Aali, courtier indépendant
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-brand-ink text-white rounded-2xl p-8">
                <div className="overline text-white">Simulation rapide</div>
                <div className="font-serif text-[44px] leading-none mt-3 tracking-tight">3 000 €</div>
                <div className="text-sm text-white/70 mt-2 leading-relaxed">
                  économisés sur votre impôt pour 10 000 € versés (TMI 30 %)
                </div>
              </div>
              <button
                data-testid="per-cta"
                onClick={() => openQuote("per")}
                className="btn-press w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink hover:bg-black text-white px-7 py-4 text-sm font-semibold tracking-[0.2em] uppercase"
              >
                Calculer ma défiscalisation <ChevronRight className="h-4 w-4" />
              </button>
              <Link
                to="/epargne-patrimoine"
                data-testid="per-discover-link"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-brand-ink/20 hover:border-brand-ink text-brand-ink px-7 py-4 text-sm font-semibold tracking-[0.2em] uppercase transition-colors"
              >
                Découvrir la page PER
              </Link>
              <ul className="space-y-2 pt-2">
                {[
                  "Audit fiscal personnalisé",
                  "Comparaison de tous les PER du marché",
                  "Sans engagement de votre part",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm text-brand-ink/75">
                    <Check className="h-4 w-4 text-brand-ink" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
